/**
 * Canonical service catalogue — the single source of truth for service slugs.
 * Used to validate lead-capture form input on the server and to drive the
 * client's service navigation/dropdowns. Rich marketing copy (long
 * descriptions, feature lists, icons) intentionally lives in
 * `client/src/constants/services.data.ts`, keyed by the same slugs, so this
 * package stays a lightweight, framework-agnostic contract.
 */
export const SERVICE_CATEGORIES = [
  { slug: 'software-development', name: 'Software Development' },
  { slug: 'design', name: 'Design' },
  { slug: 'cloud-infrastructure', name: 'Cloud & Infrastructure' },
  { slug: 'modernization', name: 'Modernization & Data' },
  { slug: 'ai-automation', name: 'AI & Automation' },
  { slug: 'security-support', name: 'Security & Support' },
  { slug: 'consulting', name: 'Consulting' },
] as const;

export type ServiceCategorySlug = (typeof SERVICE_CATEGORIES)[number]['slug'];

export const SERVICES = [
  {
    slug: 'custom-website-development',
    name: 'Custom Website Development',
    category: 'software-development',
  },
  {
    slug: 'enterprise-erp-development',
    name: 'Enterprise ERP Development',
    category: 'software-development',
  },
  { slug: 'crm-solutions', name: 'CRM Solutions', category: 'software-development' },
  {
    slug: 'mobile-app-development',
    name: 'Mobile Application Development',
    category: 'software-development',
  },
  {
    slug: 'progressive-web-apps',
    name: 'Progressive Web Applications (PWA)',
    category: 'software-development',
  },
  {
    slug: 'saas-product-development',
    name: 'SaaS Product Development',
    category: 'software-development',
  },
  { slug: 'ecommerce-solutions', name: 'E-commerce Solutions', category: 'software-development' },
  {
    slug: 'api-development-integration',
    name: 'API Development & Integration',
    category: 'software-development',
  },
  { slug: 'ui-ux-design', name: 'UI/UX Design', category: 'design' },
  { slug: 'cloud-solutions', name: 'Cloud Solutions', category: 'cloud-infrastructure' },
  { slug: 'vps-hosting', name: 'VPS Hosting', category: 'cloud-infrastructure' },
  { slug: 'cloud-hosting', name: 'Cloud Hosting', category: 'cloud-infrastructure' },
  { slug: 'dedicated-servers', name: 'Dedicated Servers', category: 'cloud-infrastructure' },
  { slug: 'domain-registration', name: 'Domain Registration', category: 'cloud-infrastructure' },
  {
    slug: 'ssl-certificate-management',
    name: 'SSL Certificate Management',
    category: 'cloud-infrastructure',
  },
  { slug: 'devops-services', name: 'DevOps Services', category: 'cloud-infrastructure' },
  { slug: 'application-migration', name: 'Application Migration', category: 'modernization' },
  {
    slug: 'legacy-system-modernization',
    name: 'Legacy System Modernization',
    category: 'modernization',
  },
  {
    slug: 'database-design-optimization',
    name: 'Database Design & Optimization',
    category: 'modernization',
  },
  { slug: 'ai-ml-solutions', name: 'AI & Machine Learning Solutions', category: 'ai-automation' },
  { slug: 'automation-solutions', name: 'Automation Solutions', category: 'ai-automation' },
  {
    slug: 'cybersecurity-solutions',
    name: 'Cybersecurity Solutions',
    category: 'security-support',
  },
  {
    slug: 'software-maintenance-support',
    name: 'Software Maintenance & Support',
    category: 'security-support',
  },
  { slug: 'technical-consulting', name: 'Technical Consulting', category: 'consulting' },
  { slug: 'digital-transformation', name: 'Digital Transformation', category: 'consulting' },
] as const satisfies ReadonlyArray<{ slug: string; name: string; category: ServiceCategorySlug }>;

export type ServiceSlug = (typeof SERVICES)[number]['slug'];

export const SERVICE_SLUGS = SERVICES.map((s) => s.slug) as [ServiceSlug, ...ServiceSlug[]];

export const BUDGET_RANGES = [
  'under_5k',
  '5k_15k',
  '15k_50k',
  '50k_150k',
  '150k_plus',
  'not_sure',
] as const;

export type BudgetRange = (typeof BUDGET_RANGES)[number];
